import { Pagination } from "react-bootstrap";

/** Show every page number when the page count is at or below this threshold. */
const MAX_UNWINDOWED_PAGES = 5;
/** Number of page buttons pinned to each edge when windowing. */
const EDGE_PAGE_COUNT = 2;

type PageItem = number | "ellipsis";

/**
 * Builds the list of page buttons to render. All pages are shown when there
 * are five or fewer; otherwise the first two, last two, and current page are
 * shown with ellipses covering the gaps.
 */
function getPageItems(pageIndex: number, pageCount: number): PageItem[] {
  if (pageCount <= MAX_UNWINDOWED_PAGES) {
    return Array.from({ length: pageCount }, (_, page) => page);
  }

  const visiblePages = new Set<number>([pageIndex]);
  for (let edge = 0; edge < EDGE_PAGE_COUNT; edge += 1) {
    visiblePages.add(edge);
    visiblePages.add(pageCount - 1 - edge);
  }

  const sortedPages = [...visiblePages].sort((a, b) => a - b);
  const items: PageItem[] = [];
  let previousPage: number | null = null;
  for (const page of sortedPages) {
    if (previousPage !== null && page - previousPage > 1) {
      items.push("ellipsis");
    }
    items.push(page);
    previousPage = page;
  }

  return items;
}

interface FeatureFlagListPaginationProps {
  pageIndex: number;
  pageCount: number;
  pageSize: number;
  filteredRowCount: number;
  canPreviousPage: boolean;
  canNextPage: boolean;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onGoToPage: (pageIndex: number) => void;
}

/** Renders entry summary and First / Previous / page / Next / Last controls. */
function FeatureFlagListPagination({
  pageIndex,
  pageCount,
  pageSize,
  filteredRowCount,
  canPreviousPage,
  canNextPage,
  onPreviousPage,
  onNextPage,
  onGoToPage,
}: FeatureFlagListPaginationProps) {
  const from = filteredRowCount === 0 ? 0 : pageIndex * pageSize + 1;
  const to = Math.min((pageIndex + 1) * pageSize, filteredRowCount);

  const pageItems = getPageItems(pageIndex, pageCount).map((item, index) =>
    item === "ellipsis" ? (
      <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
    ) : (
      <Pagination.Item
        key={item}
        active={item === pageIndex}
        onClick={() => onGoToPage(item)}
      >
        {item + 1}
      </Pagination.Item>
    )
  );

  return (
    <div className="featureFlagListPagination">
      <div className="paginationSummary">
        Showing {from} to {to} of {filteredRowCount} entries
      </div>
      <Pagination className="paginationControls mb-0">
        <Pagination.First disabled={!canPreviousPage} onClick={() => onGoToPage(0)}>
          First
        </Pagination.First>
        <Pagination.Prev disabled={!canPreviousPage} onClick={onPreviousPage}>
          Previous
        </Pagination.Prev>
        {pageItems}
        <Pagination.Next disabled={!canNextPage} onClick={onNextPage}>
          Next
        </Pagination.Next>
        <Pagination.Last
          disabled={!canNextPage}
          onClick={() => onGoToPage(pageCount - 1)}
        >
          Last
        </Pagination.Last>
      </Pagination>
    </div>
  );
}

export default FeatureFlagListPagination;
